import { Directive, ElementRef, OnInit, OnDestroy, Output, EventEmitter, Input } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { MapsAPILoader } from 'src/app/shared/services/maps-api-loader.service';
import * as _ from 'lodash';

declare var google: any;

/**
 * Angular CDK 21+ opens MatDialog with the Popover API (top layer) by default.
 * Nothing outside that popover host can paint above the dialog via z-index — which is
 * why raising .pac-container on <body> or even under .cdk-overlay-container always lost.
 * While the field is focused, keep Google's .pac-container inside the dialog's popover host.
 */
@Directive({
    selector: '[googlePlaces]',
    standalone: false
})
export class GooglePlacesDirective implements OnInit, OnDestroy {

  @Input() address: AbstractControl;
  @Input() country: AbstractControl;
  @Input() region: AbstractControl;
  @Input() city: AbstractControl;
  @Input() province: AbstractControl;
  @Input() postal_code: AbstractControl;
  @Input() street_name: AbstractControl;
  @Input() streetNumber: AbstractControl;
  @Input() latitude: AbstractControl;
  @Input() longitude: AbstractControl;
  @Input() formattedAddress: AbstractControl

  @Output() selectAddress: EventEmitter<any> = new EventEmitter();
  private readonly element: HTMLInputElement;

  private raiseTimer: ReturnType<typeof setInterval> | null = null;
  private blurTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  private readonly onFocus = (): void => {
    if (this.blurTimer) {
      clearTimeout(this.blurTimer);
      this.blurTimer = null;
    }
    this.startRaisingPac();
  };

  private readonly onBlur = (): void => {
    // Keep attached briefly so a mousedown on a suggestion still works.
    this.blurTimer = setTimeout(() => this.stopRaisingPac(), 400);
  };

  constructor(
    private elRef: ElementRef,
    private http: HttpClient,
    private mapsAPILoader: MapsAPILoader,
  ) {
    this.element = this.elRef.nativeElement;
  }

  ngOnInit(): void {
    this.mapsAPILoader.load().then(() => {
      if (this.destroyed || typeof google === 'undefined' || !google?.maps?.places) {
        return;
      }
      this.initGooglePlaces();
    });
  }

  private initGooglePlaces(): void {

    // Temporarily suppress Google Maps deprecation warning
    const originalWarn = console.warn;
    console.warn = (...args) => {
      if (typeof args[0] === 'string' && args[0].includes('google.maps.places.Autocomplete')) return;
      originalWarn.apply(console, args);
    };

    const autocomplete = new google.maps.places.Autocomplete(this.element);
    console.warn = originalWarn;

    this.element.addEventListener('focus', this.onFocus);
    this.element.addEventListener('blur', this.onBlur);

    // Bias predictions toward the user's location (like google.com/maps) so the same query
    // surfaces NEARBY addresses first instead of globally-ranked ones. This only biases the
    // ranking — it never restricts the country — and falls back to Google's default IP-based
    // bias if geolocation is denied or blocked.
    const applyBias = (lat: number, lng: number, accuracy: number = 0) => {
      try {
        // radius (meters): use the reported accuracy, but at least ~30km so the bias covers the
        // surrounding city/region rather than a single point.
        const radius = Math.max(accuracy || 0, 30000);
        const circle = new google.maps.Circle({ center: { lat, lng }, radius });
        autocomplete.setBounds(circle.getBounds());
      } catch (e) {
        // ignore — keep default predictions
      }
    };

    // 1) Apply the last-known location immediately (cached from a previous visit) so the very
    //    first keystrokes are already biased — geolocation is async and would otherwise miss
    //    fast typing on the initial load.
    try {
      const cached = JSON.parse(localStorage.getItem('geo_bias') || 'null');
      if (cached && typeof cached.lat === 'number' && typeof cached.lng === 'number') {
        applyBias(cached.lat, cached.lng, cached.accuracy);
      }
    } catch (e) { /* ignore a bad/empty cache */ }

    // 2) Resolve the user's location from their IP — the same approach google.com/maps uses, and
    //    it needs NO permission prompt. This reliably biases predictions to the user's city (e.g.
    //    a Lahore user typing "255" gets Lahore results). The /ipdata/lookup endpoint is already
    //    used elsewhere (address map) and returns { latitude, longitude }.
    this.http.get(`${environment.USER_MANAGEMENT_URL}/ipdata/lookup`).subscribe(
      (res: any) => {
        const lat = parseFloat(res?.latitude);
        const lng = parseFloat(res?.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          applyBias(lat, lng);
          try {
            localStorage.setItem('geo_bias', JSON.stringify({ lat, lng }));
          } catch (e) { /* ignore storage errors */ }
        }
      },
      () => { /* keep cached/default bias on error */ }
    );

    google.maps.event.addListener(autocomplete, 'place_changed', () => {
      // Emit the new address object for the updated place
      this.selectAddress.emit(autocomplete.getPlace());
      const address = autocomplete.getPlace();
      if (address.formatted_address) {
        this.formattedAddress?.setValue(this.element.value);
      }
      let countryName: any;
      let cityName: any;
      let ProvinceName: any;
      let postal_code: any;
      let streetName: any;
      let streetNumber: any;
      let regionName: any;

      if (this.country) {
        this.country.setValue(null);
      }
      if (this.region) {
        this.region.setValue(null);
      }
      if (this.city) {
        this.city.setValue(null);
      }
      if (this.province) {
        this.province.setValue(null);
      }
      if (this.postal_code) {
        this.postal_code.setValue(null);
      }
      if (this.street_name) {
        this.street_name.setValue(null);
      }
      if (this.streetNumber) {
        this.streetNumber.setValue(null);
      }
      if (this.latitude) {
        this.latitude.setValue(null);
      }
      if (this.longitude) {
        this.longitude.setValue(null);
      }

      postal_code = _.find(address.address_components, (component: any) => component.types.indexOf('postal_code') > -1);
      streetName = _.find(address.address_components, (component: any) => component.types.indexOf('route') > -1);
      streetNumber = _.find(address.address_components, (component: any) => component.types.indexOf('street_number') > -1);
      countryName = _.find(address.address_components, (component: any) => component.types.indexOf('country') > -1);
      cityName = _.find(address.address_components, (component: any) => component.types.indexOf('locality') > -1);
      ProvinceName = _.find(address.address_components, (component: any) => component.types.indexOf('administrative_area_level_2') > -1);
      regionName = _.find(address.address_components, (component: any) => component.types.indexOf('administrative_area_level_1') > -1);

      if (postal_code && this.postal_code) {
        this.postal_code.setValue(postal_code.long_name);
      }
      if (streetName && this.street_name) {
        this.street_name.setValue(streetName.long_name);
      }
      if (streetNumber && this.streetNumber) {
        this.streetNumber.setValue(streetNumber.long_name);
      }

      if (this.country) {
        this.country.setValue(countryName?.long_name || null);
      }
      this.region.setValue(regionName?.long_name || null);
      this.province.setValue(ProvinceName?.long_name || null);
      this.city.setValue(cityName?.long_name || null);
      if (this.longitude) {
        this.longitude.setValue(address?.geometry?.location?.lng());
      }
      if (this.latitude) {
        this.latitude.setValue(address?.geometry?.location?.lat());
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.element.removeEventListener('focus', this.onFocus);
    this.element.removeEventListener('blur', this.onBlur);
    if (this.blurTimer) {
      clearTimeout(this.blurTimer);
      this.blurTimer = null;
    }
    this.stopRaisingPac();
  }

  private startRaisingPac(): void {
    this.attachPacAboveDialog();
    if (this.raiseTimer) {
      return;
    }
    // Google recreates/repositions pac on each prediction — keep it inside the dialog host.
    this.raiseTimer = setInterval(() => this.attachPacAboveDialog(), 100);
  }

  private stopRaisingPac(): void {
    if (this.raiseTimer) {
      clearInterval(this.raiseTimer);
      this.raiseTimer = null;
    }
  }

  private attachPacAboveDialog(): void {
    // CDK 21 default: dialog host is a popover in the browser top layer.
    const popoverHost = this.element.closest(
      '.cdk-overlay-popover, [popover="manual"]'
    ) as HTMLElement | null;

    if (popoverHost) {
      this.movePacInto(popoverHost);
      return;
    }

    // Legacy path when usePopover is disabled: stack above panes inside the overlay container.
    const overlay = document.querySelector('.cdk-overlay-container') as HTMLElement | null;
    if (overlay?.querySelector('.cdk-overlay-pane')) {
      this.movePacInto(overlay);
    }
  }

  private movePacInto(target: HTMLElement): void {
    document.querySelectorAll<HTMLElement>('.pac-container').forEach((pac) => {
      if (pac.parentElement !== target || target.lastElementChild !== pac) {
        target.appendChild(pac);
      }
      // Popover/overlay hosts use pointer-events: none; opt the list back in.
      pac.style.setProperty('pointer-events', 'auto', 'important');
      // Popover host is position:fixed covering the viewport — Google's top/left still align.
      pac.style.setProperty('position', 'absolute', 'important');
      pac.style.setProperty('z-index', '10000', 'important');
    });
  }
}
